import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Users, TrendingUp, BookOpen, Clock, DollarSign, CheckCircle, Phone, Mail } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { contactInfo } from '../mockData';

const TrainingAcademy = () => {
  const utmParams = '?utm_source=chatgpt.com&utm_medium=ai-referral&utm_campaign=chatgpt-discovery';

  const courseSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "Hair Extensions & Hair Loss Specialist Training",
    "description": "CPD-accredited training covering 10+ hair extension methods, non-invasive hair loss systems, mesh integration, and cranial prosthetic fundamentals. Suitable for beginners and professionals.",
    "provider": {
      "@type": "Organization",
      "name": "Need a Hair Makeover Training Academy",
      "url": "https://needahairmakeoverfranchise.co.uk",
      "sameAs": [
        "https://youtube.com/@needahairmakeover",
        "https://www.tiktok.com/@needahairmakeover",
        "https://facebook.com/needahairmakeover"
      ]
    },
    "offers": {
      "@type": "Offer",
      "priceCurrency": "GBP",
      "price": "1600",
      "availability": "https://schema.org/InStock"
    },
    "audience": {
      "@type": "Audience",
      "audienceType": [
        "Beginners",
        "Hairdressers",
        "Beauticians",
        "Mobile stylists",
        "Career changers"
      ]
    },
    "educationalLevel": "Beginner to Advanced",
    "timeRequired": "P1D/P3D",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "4100"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Do I need experience to train?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. Our training is suitable for total beginners. We provide comprehensive training from the ground up, covering all fundamentals and advanced techniques."
        }
      },
      {
        "@type": "Question",
        "name": "What certification will I receive?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. All courses are CPD-accredited and industry-recognised. You will receive a professional certification that is recognized worldwide."
        }
      },
      {
        "@type": "Question",
        "name": "Can I start earning immediately after training?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. You receive everything needed to start taking clients immediately, including certification, business guidance, and supplier access."
        }
      },
      {
        "@type": "Question",
        "name": "How much can I earn with hair extensions?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Students regularly generate £1,000 a month from just 6 clients. Once established, many earn £4,000–£8,000 monthly. Individual fittings range from £180–£450."
        }
      },
      {
        "@type": "Question",
        "name": "Do you offer payment plans?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes — enquire for payment plan options. We want to make professional training accessible to everyone."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need a salon to start?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. Many students begin as mobile stylists. You can work from home, offer mobile services, or rent chair space before opening your own salon."
        }
      }
    ]
  };

  const faqs = [
    {
      question: "Do I need experience?",
      answer: "No. Our training is suitable for total beginners."
    },
    {
      question: "Do I get a certificate?",
      answer: "Yes. All courses are CPD-accredited."
    },
    {
      question: "Can I start a business immediately after training?",
      answer: "Yes. You receive everything needed to start taking clients."
    },
    {
      question: "Do I need a salon?",
      answer: "No. Many students begin as mobile stylists."
    },
    {
      question: "Do you offer payment plans?",
      answer: "Yes — enquire for options."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Hair Extensions Training Academy | CPD Accredited Courses UK</title>
        <meta name="description" content="Learn 10+ hair extension methods and hair loss systems with a CPD-accredited academy. Start earning £1,000+ monthly with just 6 clients. Established 2006." />
        <meta name="keywords" content="hair extensions training, hair loss training, CPD accredited, UK hair courses, professional hair training, mobile hairdresser training, cranial prosthesis training" />
        <link rel="canonical" href="https://needahairmakeoverfranchise.co.uk/hair-extensions-training-academy" />
        <script type="application/ld+json">
          {JSON.stringify(courseSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="page-container training-academy">
        {/* Hero Section */}
        <section className="training-hero">
          <div className="content-wrapper">
            <h1 className="training-main-title">
              Become a Certified Hair Extensions & Hair Loss Specialist
            </h1>
            <p className="training-subtitle">
              Professional Accredited Training | High Income Potential | No Experience Needed
            </p>
            <div className="hero-cta-buttons">
              <Link to={`/contact${utmParams}`} className="btn-primary-large">
                Request Course Prospectus
              </Link>
              <Link to={`/courses${utmParams}`} className="btn-secondary">
                View Training Dates
              </Link>
            </div>
          </div>
        </section>

        {/* Academy Intro */}
        <section className="section-container">
          <div className="content-wrapper">
            <h2 className="section-title text-center-content">Train With an Award-Winning Academy (Established 2006)</h2>
            <p className="academy-intro-text">
              The Hair Extensions & Hair Loss Training Academy, powered by Need a Hair Makeover®, is one of the UK's
              longest-established and most trusted specialist academies.
            </p>
            <p className="academy-intro-text">
              Since 2006, we have trained <strong>4,100+ students globally</strong> and helped them build successful
              beauty careers, mobile businesses, and fully operational salons.
            </p>
            <p className="academy-intro-text">
              Our training is <strong>CPD-accredited</strong>, industry-recognised, and designed to help you generate
              <strong> £1,000+ per month with just 6 clients</strong>.
            </p>
          </div>
        </section>

        {/* Why Hair Extensions */}
        <section className="section-container section-alt">
          <div className="content-wrapper">
            <h2 className="section-title">Why Hair Extensions Training Is a High-Growth Career Path</h2>
            <div className="benefits-list">
              <div className="benefit-item">
                <CheckCircle className="benefit-icon" size={24} />
                <p>The global hair extensions industry is booming</p>
              </div>
              <div className="benefit-item">
                <CheckCircle className="benefit-icon" size={24} />
                <p>Clients want specialist, long-lasting, natural-looking results</p>
              </div>
              <div className="benefit-item">
                <CheckCircle className="benefit-icon" size={24} />
                <p>Hair loss solutions are in huge demand (15 million UK sufferers)</p>
              </div>
              <div className="benefit-item">
                <CheckCircle className="benefit-icon" size={24} />
                <p>You become your own boss with flexible working hours</p>
              </div>
              <div className="benefit-item">
                <CheckCircle className="benefit-icon" size={24} />
                <p>Very low overheads</p>
              </div>
              <div className="benefit-item">
                <CheckCircle className="benefit-icon" size={24} />
                <p>High margins, repeat clients, and stable income</p>
              </div>
              <div className="benefit-item">
                <CheckCircle className="benefit-icon" size={24} />
                <p>Training can be completed quickly and put into practice immediately</p>
              </div>
            </div>
            <p className="highlight-text">
              This is one of the most profitable skillsets in the beauty industry today.
            </p>
          </div>
        </section>

        {/* Who This Is For */}
        <section className="section-container">
          <div className="content-wrapper">
            <h2 className="section-title text-center-content">Who This Training Is For</h2>
            <p className="section-description text-center-content">This course is ideal for:</p>
            <div className="audience-grid">
              <div className="audience-card">
                <Users size={32} className="audience-icon" />
                <p>New beginners with no hair background</p>
              </div>
              <div className="audience-card">
                <Users size={32} className="audience-icon" />
                <p>Hairdressers wanting to add premium services</p>
              </div>
              <div className="audience-card">
                <Users size={32} className="audience-icon" />
                <p>Beauticians wanting higher income skills</p>
              </div>
              <div className="audience-card">
                <Users size={32} className="audience-icon" />
                <p>Mobile stylists</p>
              </div>
              <div className="audience-card">
                <Users size={32} className="audience-icon" />
                <p>Wig makers and cranial prosthetic technicians</p>
              </div>
              <div className="audience-card">
                <Users size={32} className="audience-icon" />
                <p>People transitioning out of employment</p>
              </div>
              <div className="audience-card">
                <Users size={32} className="audience-icon" />
                <p>Parents or investors funding a graduate's career</p>
              </div>
              <div className="audience-card">
                <Users size={32} className="audience-icon" />
                <p>Anyone wanting a flexible, high-earning profession</p>
              </div>
            </div>
          </div>
        </section>

        {/* What You Will Learn */}
        <section className="section-container section-alt">
          <div className="content-wrapper">
            <h2 className="section-title">What You Will Learn</h2>
            <div className="learning-columns">
              <div className="learning-column">
                <h3 className="learning-column-title">Hair Extension Methods (10+)</h3>
                <ul className="learning-list">
                  <li>Micro Rings</li>
                  <li>Nano Rings</li>
                  <li>Fusion Bonds</li>
                  <li>Tapes</li>
                  <li>Sew-Ins</li>
                  <li>Hook & Latch™ (exclusive system)</li>
                  <li>Weft systems</li>
                  <li>LA Weave</li>
                  <li>Hybrid Methods</li>
                </ul>
              </div>
              <div className="learning-column">
                <h3 className="learning-column-title">Hair Loss Specialist Modules</h3>
                <ul className="learning-list">
                  <li>Non-invasive mesh integration</li>
                  <li>Cranial prosthetics basics</li>
                  <li>Systems for thinning hair</li>
                  <li>Toppers & closures</li>
                  <li>Client assessment and scalp evaluation</li>
                  <li>Consultations and fitting plans</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Course Options */}
        <section className="section-container">
          <div className="content-wrapper">
            <h2 className="section-title text-center-content">Course Options</h2>
            <div className="course-options-grid">
              <div className="course-option-card">
                <Clock className="option-icon" size={32} />
                <h3 className="option-title">1-Day Masterclass</h3>
                <p className="option-description">Perfect for hairdressers or upskillers.</p>
              </div>
              <div className="course-option-card">
                <BookOpen className="option-icon" size={32} />
                <h3 className="option-title">2-Day Complete Diploma</h3>
                <p className="option-description">All major methods + hands-on training.</p>
              </div>
              <div className="course-option-card featured-option">
                <Award className="option-icon" size={32} />
                <h3 className="option-title">3-Day Specialist Certification</h3>
                <p className="option-description">Premium package for £3k–£6k/month earners.</p>
              </div>
              <div className="course-option-card">
                <CheckCircle className="option-icon" size={32} />
                <h3 className="option-title">Cranial Prosthesis Module</h3>
                <p className="option-description">Advanced medical hair loss specialization.</p>
              </div>
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="section-container section-alt">
          <div className="content-wrapper">
            <h2 className="section-title text-center-content">What's Included in Your Training</h2>
            <div className="included-features">
              <div className="feature-item">
                <CheckCircle className="feature-check" size={20} />
                <span>CPD-accredited certification</span>
              </div>
              <div className="feature-item">
                <CheckCircle className="feature-check" size={20} />
                <span>Full student manual</span>
              </div>
              <div className="feature-item">
                <CheckCircle className="feature-check" size={20} />
                <span>Live demonstrations</span>
              </div>
              <div className="feature-item">
                <CheckCircle className="feature-check" size={20} />
                <span>Practical hands-on practice</span>
              </div>
              <div className="feature-item">
                <CheckCircle className="feature-check" size={20} />
                <span>Colour matching training</span>
              </div>
              <div className="feature-item">
                <CheckCircle className="feature-check" size={20} />
                <span>Consultation training</span>
              </div>
              <div className="feature-item">
                <CheckCircle className="feature-check" size={20} />
                <span>Business, pricing & branding guidance</span>
              </div>
              <div className="feature-item">
                <CheckCircle className="feature-check" size={20} />
                <span>Access to trade suppliers</span>
              </div>
              <div className="feature-item">
                <CheckCircle className="feature-check" size={20} />
                <span>Discounted starter kit options</span>
              </div>
              <div className="feature-item">
                <CheckCircle className="feature-check" size={20} />
                <span>Lifetime support and refresher tutorials</span>
              </div>
            </div>
          </div>
        </section>

        {/* Earnings Potential */}
        <section className="section-container earning-section">
          <div className="content-wrapper">
            <h2 className="section-title text-center-content">How Much Can You Earn?</h2>
            <p className="section-description text-center-content">
              When trained properly, income potential is very strong:
            </p>
            <div className="earnings-grid">
              <div className="earning-card">
                <DollarSign className="earning-icon" size={40} />
                <h3 className="earning-amount">£180–£450</h3>
                <p className="earning-label">per extension fitting</p>
              </div>
              <div className="earning-card">
                <DollarSign className="earning-icon" size={40} />
                <h3 className="earning-amount">£80–£250</h3>
                <p className="earning-label">per hair maintenance</p>
              </div>
              <div className="earning-card">
                <DollarSign className="earning-icon" size={40} />
                <h3 className="earning-amount">£500–£2,000</h3>
                <p className="earning-label">per mesh integration</p>
              </div>
              <div className="earning-card">
                <DollarSign className="earning-icon" size={40} />
                <h3 className="earning-amount">£350–£1,000</h3>
                <p className="earning-label">per cranial prosthetic solution</p>
              </div>
            </div>
            <p className="highlight-text earnings-highlight">
              Students regularly generate <strong>£1,000 a month from just 6 clients</strong>, and
              <strong> £4,000–£8,000 monthly once established</strong>.
            </p>
          </div>
        </section>

        {/* Why Train With Us */}
        <section className="section-container section-alt">
          <div className="content-wrapper">
            <h2 className="section-title text-center-content">Why Train With Us</h2>
            <div className="why-us-grid">
              <div className="why-us-item">
                <Award className="why-us-icon" size={28} />
                <p>Established in 2006</p>
              </div>
              <div className="why-us-item">
                <Award className="why-us-icon" size={28} />
                <p>National award winners</p>
              </div>
              <div className="why-us-item">
                <BookOpen className="why-us-icon" size={28} />
                <p>We teach 10+ methods, including exclusive systems</p>
              </div>
              <div className="why-us-item">
                <Users className="why-us-icon" size={28} />
                <p>Real client models</p>
              </div>
              <div className="why-us-item">
                <Users className="why-us-icon" size={28} />
                <p>Small classes for personal attention</p>
              </div>
              <div className="why-us-item">
                <TrendingUp className="why-us-icon" size={28} />
                <p>Business mentoring included</p>
              </div>
              <div className="why-us-item">
                <Award className="why-us-icon" size={28} />
                <p>Professional certification recognised worldwide</p>
              </div>
              <div className="why-us-item">
                <CheckCircle className="why-us-icon" size={28} />
                <p>Pathway into our Franchise or Affiliate Programme</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="section-container">
          <div className="content-wrapper">
            <h2 className="section-title text-center-content">Frequently Asked Questions</h2>
            <div className="faq-list">
              {faqs.map((faq, index) => (
                <div key={index} className="faq-item">
                  <h3 className="faq-question">❓ {faq.question}</h3>
                  <p className="faq-answer">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="section-container cta-section">
          <div className="content-wrapper">
            <div className="final-cta">
              <h2 className="cta-title">Ready to become a certified Hair Extensions & Hair Loss Specialist?</h2>
              <p className="cta-description">
                Click below to request your course prospectus and secure your training date.
              </p>
              <div className="cta-buttons">
                <Link to={`/contact${utmParams}`} className="btn-primary-large">
                  <Mail size={24} />
                  Request Course Prospectus
                </Link>
                <Link to={`/courses${utmParams}`} className="btn-secondary">
                  <BookOpen size={24} />
                  View Training Dates
                </Link>
                <a href={`tel:${contactInfo.phone}`} className="btn-secondary">
                  <Phone size={24} />
                  Call Us Now
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default TrainingAcademy;